/**
 * Subscriptions Page
 * Manage recurring billing subscriptions
 */

import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import {
  subscribeToSubscriptions,
  pauseSubscription,
  resumeSubscription,
  cancelSubscription,
  deleteSubscription,
  generateSubscriptionInvoice,
} from '../services/subscriptionService';
import SubscriptionForm from '../components/SubscriptionForm';
import '../components/Modal.css';
import './Subscriptions.css';

const Subscriptions = () => {
  const user = useAuthStore((state) => state.user);
  const [subscriptions, setSubscriptions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (!user) return;

    const unsubscribe = subscribeToSubscriptions(user.uid, (data) => {
      setSubscriptions(data);
    });

    return () => unsubscribe();
  }, [user]);

  const handleCreate = () => {
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (subscription) => {
    setEditingId(subscription.id);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handlePause = async (id) => {
    if (!window.confirm('Pause this subscription? No invoices will be generated until resumed.')) return;
    setActionLoading(id);
    try {
      await pauseSubscription(id);
    } catch (error) {
      alert('Error: ' + error.message);
    }
    setActionLoading(null);
  };

  const handleResume = async (id) => {
    setActionLoading(id);
    try {
      await resumeSubscription(id);
    } catch (error) {
      alert('Error: ' + error.message);
    }
    setActionLoading(null);
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this subscription permanently? This cannot be undone.')) return;
    setActionLoading(id);
    try {
      await cancelSubscription(id);
    } catch (error) {
      alert('Error: ' + error.message);
    }
    setActionLoading(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this subscription? This will remove all history.')) return;
    setActionLoading(id);
    try {
      await deleteSubscription(id);
    } catch (error) {
      alert('Error: ' + error.message);
    }
    setActionLoading(null);
  };

  const handleGenerateInvoice = async (id) => {
    if (!window.confirm('Generate an invoice now for this subscription?')) return;
    setActionLoading(id);
    try {
      const result = await generateSubscriptionInvoice(id);
      alert(`Invoice generated successfully! Invoice ID: ${result.invoiceId}`);
    } catch (error) {
      alert('Error generating invoice: ' + error.message);
    }
    setActionLoading(null);
  };

  const filteredSubscriptions = subscriptions.filter((sub) => {
    if (filter === 'all') return true;
    return sub.status === filter;
  });

  const formatDate = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString();
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      paused: 'badge-warning',
      cancelled: 'badge-danger',
    };
    return badges[status] || 'badge-secondary';
  };

  const getFrequencyLabel = (frequency) => {
    const labels = {
      weekly: 'Weekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly',
    };
    return labels[frequency] || frequency;
  };

  return (
    <div className="subscriptions-page">
      <div className="page-header">
        <div>
          <h1>Recurring Subscriptions</h1>
          <p className="subtitle">Manage recurring billing for your clients</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreate}>
          + New Subscription
        </button>
      </div>

      {/* Stats Summary */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{subscriptions.filter(s => s.status === 'active').length}</div>
          <div className="stat-label">Active</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{subscriptions.filter(s => s.status === 'paused').length}</div>
          <div className="stat-label">Paused</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {formatCurrency(
              subscriptions
                .filter(s => s.status === 'active')
                .reduce((sum, s) => sum + (s.amount || 0), 0),
              'PHP'
            )}
          </div>
          <div className="stat-label">Monthly Recurring</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({subscriptions.length})
        </button>
        <button
          className={`filter-tab ${filter === 'active' ? 'active' : ''}`}
          onClick={() => setFilter('active')}
        >
          Active
        </button>
        <button
          className={`filter-tab ${filter === 'paused' ? 'active' : ''}`}
          onClick={() => setFilter('paused')}
        >
          Paused
        </button>
        <button
          className={`filter-tab ${filter === 'cancelled' ? 'active' : ''}`}
          onClick={() => setFilter('cancelled')}
        >
          Cancelled
        </button>
      </div>

      {/* Subscriptions List */}
      {filteredSubscriptions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No subscriptions found</h3>
          <p>
            {filter === 'all'
              ? 'Create your first recurring subscription to automate billing.'
              : `No ${filter} subscriptions.`}
          </p>
          {filter === 'all' && (
            <button className="btn btn-primary" onClick={handleCreate}>
              Create Subscription
            </button>
          )}
        </div>
      ) : (
        <div className="subscriptions-list">
          {filteredSubscriptions.map((sub) => (
            <div key={sub.id} className="subscription-card">
              <div className="subscription-header">
                <div className="subscription-client">
                  <h3>{sub.clientName}</h3>
                  <span className="client-email">{sub.clientEmail}</span>
                </div>
                <span className={`badge ${getStatusBadge(sub.status)}`}>
                  {sub.status}
                </span>
              </div>

              <div className="subscription-details">
                <div className="detail-row">
                  <span className="detail-label">Amount</span>
                  <span className="detail-value amount">
                    {formatCurrency(sub.amount, sub.currency)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Frequency</span>
                  <span className="detail-value">{getFrequencyLabel(sub.frequency)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Next Billing</span>
                  <span className="detail-value">{formatDate(sub.nextBillingDate)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Last Billed</span>
                  <span className="detail-value">{formatDate(sub.lastBillingDate)}</span>
                </div>
              </div>

              {sub.description && (
                <div className="subscription-description">
                  {sub.description}
                </div>
              )}

              <div className="subscription-actions">
                {sub.status === 'active' && (
                  <>
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => handleGenerateInvoice(sub.id)}
                      disabled={actionLoading === sub.id}
                    >
                      Generate Invoice
                    </button>
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => handleEdit(sub)}
                      disabled={actionLoading === sub.id}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => handlePause(sub.id)}
                      disabled={actionLoading === sub.id}
                    >
                      Pause
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleCancel(sub.id)}
                      disabled={actionLoading === sub.id}
                    >
                      Cancel
                    </button>
                  </>
                )}
                {sub.status === 'paused' && (
                  <>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleResume(sub.id)}
                      disabled={actionLoading === sub.id}
                    >
                      Resume
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleCancel(sub.id)}
                      disabled={actionLoading === sub.id}
                    >
                      Cancel
                    </button>
                  </>
                )}
                {sub.status === 'cancelled' && (
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(sub.id)}
                    disabled={actionLoading === sub.id}
                  >
                    Delete
                  </button>
                )}
                {actionLoading === sub.id && (
                  <span className="action-loading">Processing...</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Subscription Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={handleFormCancel}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit Subscription' : 'Create Subscription'}</h2>
              <button className="modal-close" onClick={handleFormCancel}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <SubscriptionForm
                userId={user.uid}
                subscriptionId={editingId}
                onSuccess={handleFormSuccess}
                onCancel={handleFormCancel}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;
